import '@src/assets/custom-grid-theme.css';
import '@src/index.css';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

import { ApolloProvider } from '@apollo/client';
import { createTheme, ThemeProvider } from '@ds/coolshop';
import { LanguageProvider } from '@modules/language';
import { grey } from '@mui/material/colors';
import { useAuth } from '@src/hooks';
import { SnackbarProvider } from 'notistack';
import { StrictMode, useContext, useEffect, useMemo, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { IntlProvider, MessageFormatElement } from 'react-intl';

import { IAuthContext } from './assets/types/auth';
import { DirtyContextProvider } from './components/hierarchies/shared/DirtyContext';
import { Routes } from './components/layout/Routes';
import { LinearLoader } from './components/shared/LinearLoader';
import { SnackbarUtilsConfigurator } from './components/shared/SnackBarUtils';
import { SettingProvider } from './modules/setting/SettingProvider';
import { SettingsContext } from './modules/setting/SettingsContext';
import BlockActionContextProvider from './store/BlockActionContext/BlockActionContext.provider';
import JobSubscriptionContextProvider from './store/JobSubscriptionContext/JobSubscriptionContext.provider';
import { getApolloClient } from './utils/generic/getApolloClient';
// fix type
type LangImportType = typeof import('@src/assets/lang/compiled-lang/fr.json');

const loadLocaleData = async (locale: string): Promise<LangImportType> => {
    // TODO REST call per lang.json
    switch (locale) {
        case 'fr':
            return (await import('@src/assets/lang/compiled-lang/fr.json')).default;
        case 'it':
            return (await import('@src/assets/lang/compiled-lang/it.json')).default;
        default:
            return (await import('@src/assets/lang/compiled-lang/en.json')).default;
    }
};

const locale = navigator.language.substring(0, 2);

declare module '@mui/material/styles' {
    interface Palette {
        disabled: string;
    }
    interface PaletteOptions {
        disabled: string;
    }

    interface ZIndex {
        topComponent: number;
    }
}

declare module '@mui/material/Button' {
    interface ButtonPropsColorOverrides {
        disabled: true;
    }
}
function ThemedApp({ sessionData }: { sessionData: IAuthContext['sessionData'] }) {
    const { theme: themeSettings } = useContext(SettingsContext);
    // const [darkMode, setDarkMode] = useState<boolean>(localStorage.getItem('darkMode') === 'true');
    const darkMode = false;
    // useEffect(() => {
    //     localStorage.setItem('darkMode', String(darkMode));
    // }, [darkMode]);

    /**
     * Use this theme along all the app
     * - primary: main/light/dark blue
     * - secondary: main/light/dark orange
     */

    const theme = useMemo(
        () =>
            createTheme({
                palette: {
                    action: {},
                    contrastAction: {},
                    mode: darkMode ? 'dark' : 'light',
                    primary: {
                        main:
                            sessionData?.attributes?.primaryColor ??
                            themeSettings?.primaryColor ??
                            '#001560',
                    },
                    secondary: {
                        main:
                            sessionData?.attributes?.secondaryColor ??
                            themeSettings?.secondaryColor ??
                            '#057bff',
                    },
                    disabled: grey[200],
                },
                components: {
                    MuiListItemText: { styleOverrides: { primary: { fontSize: '0.9em' } } },
                    MuiFormControl: {
                        defaultProps: {
                            size: 'small',
                        },
                    },
                    MuiButton: {
                        defaultProps: {
                            size: 'small',
                        },
                    },
                },
                zIndex: {
                    topComponent: 2000,
                },
            }),
        [darkMode, sessionData],
    );

    return (
        <ThemeProvider theme={theme}>
            <DirtyContextProvider>
                <BlockActionContextProvider>
                    <DndProvider backend={HTML5Backend}>
                        <SnackbarProvider maxSnack={3}>
                            <JobSubscriptionContextProvider>
                                <SnackbarUtilsConfigurator />
                                <Routes />
                            </JobSubscriptionContextProvider>
                        </SnackbarProvider>
                    </DndProvider>
                </BlockActionContextProvider>
            </DirtyContextProvider>
        </ThemeProvider>
    );
}
function App(): JSX.Element {
    const { initialize, isReady, error, sessionData } = useAuth();

    const [messages, setMessages] = useState<
        Record<string, string> | Record<string, MessageFormatElement[]>
    >({});

    useEffect(() => {
        initialize();
    }, []);

    useEffect(() => {
        if (!isReady) return;
        loadLocaleData(locale).then(setMessages);
    }, [isReady]);

    const apolloClient = getApolloClient();

    return !isReady ? (
        <LinearLoader message={error?.message} /> // TODO: remove this string
    ) : (
        <StrictMode>
            <ApolloProvider client={apolloClient}>
                <SettingProvider>
                    <LanguageProvider>
                        <IntlProvider
                            messages={messages}
                            locale={locale}
                            defaultLocale="en"
                            onError={err => {
                                if (err.code === 'MISSING_TRANSLATION') {
                                    // TODO: log the error
                                    return;
                                }
                                // eslint-disable-next-line no-console
                                console.error(err);
                            }}
                        >
                            <ThemedApp sessionData={sessionData} />
                        </IntlProvider>
                    </LanguageProvider>
                </SettingProvider>
            </ApolloProvider>
        </StrictMode>
    );
}

export default App;
